import Vue from 'vue';
import Vuex, { StoreOptions } from 'vuex';
import { RootState } from './types';
import { actions } from './actions';
import { mutations } from './mutations';
import { auth } from './modules/auth/index';
import { user } from './modules/user/index';
import { userGroup } from './modules/user_group/index';
import { nodeDomain } from './modules/node_domain/index';
import { domainAccount } from './modules/domain_account/index';
import { nodeIdp } from './modules/node_idp/index';
import { idpAccount } from './modules/idp_account/index';
import { idpType } from './modules/idp_type/index';
import { node } from './modules/node/index';
import { nodeAutoLoginDefinition } from './modules/node/auto_login_definition/index';
import { nodeDomainAccount } from './modules/node/domain_account/index';
import { nodeNodeLoginHistoryExcludeCondition } from './modules/node/node_login_history_exclude_condition/index';
import { nodeOsAccount } from './modules/node/os_account/index';
import { nodeOtherAccount } from './modules/node/other_account/index';
import { nodeIdpAccount } from './modules/node/idp_account/index';
import { nodeGroup } from './modules/node_group/index';
import { nodeAccount } from './modules/node_account/index';
import { userSetting } from './modules/user_setting/index';
import { mailSetting } from './modules/mail_setting/index';
import { nodeType } from './modules/node_type/index';
import { multifactorAuth } from './modules/multifactor_auth/index';
import { systemChangeHistory } from './modules/system_change_history/index';
import { jobExecutionHistory } from './modules/job_execution_history/index';
import { csv } from './modules/csv/index';
import { detectionRule } from './modules/detection_rule/index';
import { detectionRuleGroup } from './modules/detection_rule_group/index';
import { workflow } from './modules/workflow/index';
import { businessSystem } from './modules/business_system/index';
import { applicationTemplate } from './modules/business_system/application_template/index';
import { routineWorkTemplate } from './modules/business_system/routine_work_template/index';
import { accountSet } from './modules/business_system/account_set/index';
import { autoLoginApp } from './modules/auto_login_app/index';
import { applicationFormTemplate } from './modules/application_form_template/index';
import { applicationForm } from './modules/application_form/index';
import { waitingApplication } from './modules/waiting_application/index';
import { privilegedIdSetting } from './modules/privileged_id_setting/index';
import { applicationFormNumber } from './modules/application_form_number_format/index';
import { privilegeIdLendingRecord } from './modules/privilege_id_lending_record/index';
import { detectionRulesRecord } from './modules/detection_rules_record/index';
import { loginRecord } from './modules/login_record/index';
import { dashboard } from './modules/dashboard/index';
import { setting } from './modules/setting/index';
import { fromAddressList } from './modules/from_address_list/index';
import { recSetting } from './modules/rec_setting/index';
import { license } from './modules/license/index';
import { productVersion } from './modules/product_version/index';
import { externalProgramUsage } from './modules/external_program_usage/index';
import { executionTimingLists } from './modules/execution_timing_lists/index';
import { loginFailureRecord } from './modules/login_failure_record/index';
import { loginType } from './modules/login_type/index';
import { nodeLoginHistoryExcludeCondition } from './modules/node_login_history_exclude_condition/index';
import { accountInventoryResultDiff } from './modules/account_inventory_result_diff/index';
import { gateway } from './modules/gateway/index';
import { accountInventorySetting } from './modules/account_inventory_setting/index';
import { accountInventoryResult } from './modules/account_inventory_result/index';
import { timeZone } from './modules/time_zone/index';
import { EmbeddedData } from './embedded-data';

Vue.use(Vuex);

const isRootEmbeddedError = EmbeddedData.authErrorStatus >= 500;

export const modules = {
    auth,
    user,
    userGroup,
    nodeDomain,
    domainAccount,
    nodeIdp,
    idpAccount,
    idpType,
    node,
    nodeAutoLoginDefinition,
    nodeDomainAccount,
    nodeNodeLoginHistoryExcludeCondition,
    nodeOsAccount,
    nodeOtherAccount,
    nodeIdpAccount,
    nodeAccount,
    nodeGroup,
    nodeType,
    userSetting,
    mailSetting,
    multifactorAuth,
    systemChangeHistory,
    jobExecutionHistory,
    csv,
    detectionRule,
    detectionRuleGroup,
    workflow,
    businessSystem,
    applicationTemplate,
    routineWorkTemplate,
    accountSet,
    autoLoginApp,
    applicationFormTemplate,
    applicationForm,
    waitingApplication,
    privilegedIdSetting,
    applicationFormNumber,
    privilegeIdLendingRecord,
    detectionRulesRecord,
    loginRecord,
    dashboard,
    setting,
    fromAddressList,
    recSetting,
    license,
    productVersion,
    externalProgramUsage,
    executionTimingLists,
    loginFailureRecord,
    loginType,
    gateway,
    nodeLoginHistoryExcludeCondition,
    accountInventoryResultDiff,
    accountInventorySetting,
    accountInventoryResult,
    timeZone,
};

const store: StoreOptions<RootState> = {
    strict: process.env.NODE_ENV !== 'production',
    state: {
        version: '1.0.0',
        message: undefined,
        error: false,
        errorCode: undefined,
        errorMsg: undefined,
        process: {
            running: false,
            msgKey: undefined,
        },
        embedded: {
            error: isRootEmbeddedError,
            errorCode: isRootEmbeddedError ? EmbeddedData.authErrorCode : undefined,
            errorMsg: isRootEmbeddedError ? EmbeddedData.authErrorMsg : undefined,
            adminOneTitle: EmbeddedData.adminOneTitle,
        },
    },
    actions,
    mutations: mutations,
    modules: modules,
};

export default new Vuex.Store<RootState>(store);
